// school-ai/src/i18n/t.ts
//
// 画面の文言は日本語の原文をそのままキーにして t() で引く。
//   t("保存しました")                       → 英語なら "Saved"
//   t("残り{count}件", { count })            → "{count} left"（{name} を値で置き換える）
//   tx("<0>{name}</0>さんの提出", { 0: <b />, name }) → JSX を含む文言
// 辞書（messages/en）に無いキーは原文（日本語）をそのまま返すので、
// 新しい文言を足したときに英語が未訳でも画面は壊れない。
import {
  Fragment,
  cloneElement,
  createElement,
  isValidElement,
  type ReactNode
} from "react";
import { getLanguage, resources, type Lang } from "./config";

export type TranslateValues = Record<string, unknown>;
/** 辞書の値。単複の出し分けなど、値によって文言を変えるときは関数にする */
type MessageValue = string | ((values: TranslateValues) => string);
export type Messages = Record<string, MessageValue>;

const catalogs: Partial<Record<Lang, Messages>> = {};

export const registerMessages = (lang: Lang, messages: Messages): void => {
  catalogs[lang] = { ...catalogs[lang], ...messages };
};

/**
 * 言語の辞書を読み込む。日本語は原文がそのままキーなので辞書を持たない。
 * モジュール読み込み時に t() で確定する文言があるため、アプリ本体より先に呼ぶ
 * （main.tsx）。
 */
export const loadMessages = async (lang: Lang): Promise<void> => {
  if (lang === "ja" || catalogs[lang]) return;
  const { enMessages } = await import("./messages/en");
  registerMessages("en", enMessages);
};

const PLACEHOLDER = /\{([A-Za-z0-9_]+)\}/g;

const hasValue = (values: object, name: string): boolean =>
  Object.prototype.hasOwnProperty.call(values, name);

/** 文言中の {name} を values の値で置き換える。値が無いものはそのまま残す */
export const interpolate = (
  template: string,
  values?: TranslateValues
): string => {
  if (!values) return template;
  return template.replace(PLACEHOLDER, (match, name: string) =>
    hasValue(values, name) ? String(values[name]) : match
  );
};

const resolveNamespaced = (dict: unknown, path: string): string | undefined => {
  const value = path
    .split(".")
    .reduce<unknown>(
      (acc, segment) =>
        acc && typeof acc === "object"
          ? (acc as Record<string, unknown>)[segment]
          : undefined,
      dict
    );
  return typeof value === "string" ? value : undefined;
};

const lookup = (key: string, lang: Lang): MessageValue | undefined => {
  // 名前空間付きの辞書（locales/*.ts）は "login:errors.temporary" の形で引く
  const idx = key.indexOf(":");
  if (idx > 0) {
    const ns = key.slice(0, idx);
    const path = key.slice(idx + 1);
    const nested =
      resolveNamespaced(resources[lang][ns], path) ??
      resolveNamespaced(resources.ja[ns], path);
    if (nested !== undefined) return nested;
  }
  return catalogs[lang]?.[key];
};

const render = (
  key: string,
  values: TranslateValues | undefined,
  lang: Lang
): string => {
  const message = lookup(key, lang) ?? key;
  return typeof message === "function" ? message(values ?? {}) : message;
};

/** 原文（日本語）をキーに、現在の言語の文言を返す。辞書に無ければ原文を返す */
export const t = (key: string, values?: TranslateValues): string =>
  interpolate(render(key, values, getLanguage()), values);

/** 英語の単複を出し分ける。辞書側で `v => plural(v.count, "{count} app", "{count} apps")` のように使う */
export const plural = (count: unknown, one: string, other: string): string =>
  Number(count) === 1 ? one : other;

// <0>…</0> / <0/> は values の要素、{name} は values の値
const RICH_TOKEN =
  /<([0-9]+)>([\s\S]*?)<\/\1>|<([0-9]+)\s*\/>|\{([A-Za-z0-9_]+)\}/g;

const renderRich = (
  text: string,
  values: Record<string, ReactNode>
): ReactNode => {
  const parts: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(RICH_TOKEN)) {
    const index = match.index ?? 0;
    const [whole, pairName, inner, selfName, valueName] = match;
    const name = pairName ?? selfName ?? valueName;
    if (!hasValue(values, name)) continue;
    if (index > last) parts.push(text.slice(last, index));
    const value = values[name];
    if (pairName !== undefined || selfName !== undefined) {
      const children =
        pairName !== undefined ? renderRich(inner, values) : undefined;
      parts.push(
        isValidElement(value)
          ? children === undefined
            ? cloneElement(value, { key: key++ })
            : cloneElement(value, { key: key++ }, children)
          : createElement(Fragment, { key: key++ }, children ?? value)
      );
    } else {
      parts.push(
        isValidElement(value)
          ? createElement(Fragment, { key: key++ }, value)
          : value
      );
    }
    last = index + whole.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 ? parts[0] : parts;
};

/**
 * JSX を含む文言。原文では要素を <0>…</0>（子要素あり）または <0/>（無し）、
 * 値を {name} で書き、values に同じ名前で渡す。
 *   tx("<0>{name}</0>さんの提出", { 0: <strong />, name })
 */
export const tx = (
  key: string,
  values?: Record<string, ReactNode>
): ReactNode => {
  const text = render(key, values, getLanguage());
  return values ? renderRich(text, values) : text;
};
